import * as React from 'react';
import {useSelector} from 'react-redux';

import {Session} from 'nutshell-core/session';

// @ts-expect-error - imported from .js/.jsx file

import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailSeriesIcon} from 'shells/icon/svg/email-series-icon';
import {PuzzleIcon} from 'shells/icon/svg/puzzle-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TemplateIcon} from 'shells/icon/svg/template-icon';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem, SubmenuNavItem} from 'shells/navigation/types';

import type {NutshellState} from '../../store';

import {hasEmailSequenceAutomation} from './has-email-sequence-automation';
import {hasEmailSequenceCapability} from './has-email-sequence-capability';
import {useGetEmailStats} from './use-get-email-stats';

export function EmailPageSidebar() {
    const {emailStats, isLoading, error, includeAdditionalAutomationBoxes} = useGetEmailStats();
    const userPermissions = useSelector((state: NutshellState) =>
        Session.getUserPermissions(state)
    );

    const isAutomatedSendingPaused = () => {
        return emailStats && emailStats.isAutomatedSendingPaused;
    };

    const getAutomationLinks = (): SubmenuNavItem[] => {
        const shouldShowPausedIcon = isAutomatedSendingPaused();

        const automationLinks: SubmenuNavItem[] = [
            {
                id: 'willSend',
                label: 'Scheduled',
                to: '/email/automations/sending',
                warningTooltip: shouldShowPausedIcon ? 'Email sending is paused' : undefined,
                count:
                    !isLoading && emailStats && !error
                        ? emailStats.automatedEmailsWillSendCount
                        : undefined,
                requiresUpgrade: !hasEmailSequenceCapability(),
            },
        ];

        if (includeAdditionalAutomationBoxes) {
            const needsAttentionCount =
                !isLoading && emailStats && !error
                    ? emailStats.automatedEmailsNeedsAttentionCount
                    : undefined;

            automationLinks.push(
                {
                    id: 'readyToSend',
                    label: 'Ready to send',
                    to: '/email/automations/ready-to-send',
                    count:
                        !isLoading && emailStats && !error
                            ? emailStats.automatedEmailsReadyToSendCount
                            : undefined,
                },
                {
                    id: 'needsAttention',
                    label: 'Need attention',
                    to: '/email/automations/needs-attention',
                    count: needsAttentionCount,
                }
            );
        }

        return automationLinks;
    };

    const getMessageLinks = (): SubmenuNavItem[] => {
        const hasScheduledEmailsCapability = hasEmailSequenceCapability();
        const shouldShowPausedIcon = isAutomatedSendingPaused();

        return [
            {
                id: 'inbox',
                label: 'Inbox',
                to: '/email/inbox',
            },
            {
                id: 'sent',
                label: 'Sent',
                to: '/email/sent',
            },
            {
                id: 'scheduledMail',
                label: 'Scheduled',
                to: '/email/scheduled',
                requiresUpgrade: !hasScheduledEmailsCapability,
                warningTooltip: shouldShowPausedIcon ? 'Email sending is paused' : undefined,
                count:
                    hasScheduledEmailsCapability && !isLoading && emailStats && !error
                        ? emailStats.emailsScheduledTodayCount
                        : undefined,
            },
            {
                id: 'failed',
                label: 'Failed',
                to: '/email/failed',
                count: isLoading || error || !emailStats ? undefined : emailStats.emailsFailedCount,
            },
        ];
    };

    const getExtensionsLinks = (): SubmenuNavItem[] => {
        return [
            {
                id: 'google',
                label: 'Google',
                to: '/email/google',
            },
            {
                id: 'outlook',
                label: 'Outlook',
                to: '/email/outlook',
            },
        ];
    };

    const getNavItems = (): NavItem[] => {
        const links: NavItem[] = [
            {
                id: 'email',
                label: 'Messages',
                to: '/email/inbox',
                submenu: getMessageLinks(),
                icon: EmailIcon,
            },
        ];

        // @ts-expect-error
        if (hasEmailSequenceAutomation) {
            links.push({
                id: 'automations',
                label: 'Sequences',
                to: '/email/templates',
                submenu: getAutomationLinks(),
                icon: EmailSeriesIcon,
            });
        }

        links.push({
            id: 'extensions',
            label: 'Extensions',
            to: '/email/google',
            submenu: getExtensionsLinks(),
            icon: PuzzleIcon,
        });

        // @ts-expect-error
        if (userPermissions.canAccessCrm) {
            links.push({
                id: 'email-templates',
                label: 'Email templates',
                to: '/sales/email/templates',
                isExternalLink: true,
                icon: TemplateIcon,
            });
        }

        return links;
    };

    return (
        <SecondarySidebar navItems={getNavItems()} isLoading={!userPermissions || !emailStats} />
    );
}
